// 🔐 Session Device Management Utility
// Handles single-device login enforcement

import jwtTokenService from "./jwtTokenService";
import { API_CONFIG } from "../config/api.config";

class SessionDeviceManager {
  constructor() {
    this.API_BASE_URL = API_CONFIG.AUTH_URL;
  }

  // ============================================
  // DEVICE & SESSION VALIDATION
  // ============================================

  /**
   * Validate device and session during login
   * Returns: { allowed: boolean, message: string, existingSessions: array }
   */
  async validateDeviceLogin(email, password) {
    try {
      const deviceId = jwtTokenService.getDeviceId();

      const response = await fetch(`${this.API_BASE_URL}/validate-device`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          deviceId,
          timestamp: Date.now(),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        // If API not implemented, allow login
        return { allowed: true, isNewDevice: false };
      }
    } catch (error) {
      console.error("Device validation error:", error);
      // Allow login if API is unavailable
      return { allowed: true, isNewDevice: false };
    }
  }

  /**
   * Register new device session after successful login
   */
  async registerDeviceSession(userId, email, token) {
    try {
      const deviceId = jwtTokenService.getDeviceId();
      const sessionId = jwtTokenService.getSessionId();

      const deviceInfo = this.getDeviceInfo();

      const response = await fetch(`${this.API_BASE_URL}/register-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          email,
          deviceId,
          sessionId,
          deviceInfo,
          loginTimestamp: Date.now(),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (error) {
      console.error("Session registration error:", error);
    }
  }

  /**
   * Terminate other sessions (force single device login)
   */
  async terminateOtherSessions(userId, token) {
    try {
      const currentDeviceId = jwtTokenService.getDeviceId();
      const currentSessionId = jwtTokenService.getSessionId();

      const response = await fetch(`${this.API_BASE_URL}/terminate-sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          currentDeviceId,
          currentSessionId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }
    } catch (error) {
      console.error("Session termination error:", error);
    }
  }

  /**
   * Check if session is still valid on server
   */
  async validateSession(token) {
    try {
      const deviceId = jwtTokenService.getDeviceId();
      const sessionId = jwtTokenService.getSessionId();

      const response = await fetch(`${this.API_BASE_URL}/validate-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          deviceId,
          sessionId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.valid || false;
      }

      return false;
    } catch (error) {
      console.error("Session validation error:", error);
      return false;
    }
  }

  /**
   * Logout and clear session on server
   */
  async logoutSession(token) {
    try {
      const deviceId = jwtTokenService.getDeviceId();
      const sessionId = jwtTokenService.getSessionId();

      const response = await fetch(`${this.API_BASE_URL}/logout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          deviceId,
          sessionId,
        }),
      });

      if (response.ok) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("Logout session error:", error);
      return false;
    }
  }

  // ============================================
  // DEVICE INFORMATION
  // ============================================

  getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      colorDepth: window.screen.colorDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: navigator.deviceMemory,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
    };
  }

  getDeviceName() {
    const ua = navigator.userAgent;

    // Detect device type
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return "Tablet";
    }
    if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua
      )
    ) {
      return "Mobile";
    }
    return "Desktop";
  }

  getBrowserName() {
    const ua = navigator.userAgent;

    if (ua.indexOf("Firefox") > -1) return "Firefox";
    if (ua.indexOf("Chrome") > -1) return "Chrome";
    if (ua.indexOf("Safari") > -1) return "Safari";
    if (ua.indexOf("Edge") > -1) return "Edge";
    if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) return "Opera";

    return "Unknown Browser";
  }

  getOSName() {
    const ua = navigator.userAgent;

    if (ua.indexOf("Win") > -1) return "Windows";
    if (ua.indexOf("Mac") > -1) return "MacOS";
    if (ua.indexOf("Linux") > -1) return "Linux";
    if (ua.indexOf("Android") > -1) return "Android";
    if (
      ua.indexOf("iOS") > -1 ||
      ua.indexOf("iPhone") > -1 ||
      ua.indexOf("iPad") > -1
    )
      return "iOS";

    return "Unknown OS";
  }

  getDeviceDescription() {
    return `${this.getDeviceName()} - ${this.getBrowserName()} on ${this.getOSName()}`;
  }
}

// Create singleton instance
const sessionDeviceManager = new SessionDeviceManager();

export default sessionDeviceManager;
